/**
 * SPATIAL_FILTER (AcDbSpatialFilter / AcDbFilter) type vocabulary.
 *
 * EPIC-VIEWPORT-CLIP SLICE 2 — `S-OBJECTS-SPATIAL-FILTER`.
 *
 * The SPATIAL_FILTER object is the OBJECTS-section record that backs
 * XCLIP / VPCLIP boundaries. It is owned (via the `SPATIAL` /
 * `ACAD_FILTER` extension-dictionary chain) by the block reference
 * (INSERT) or paper-space VIEWPORT it clips, and stores the clip polygon
 * plus the block-to-WCS transform matrices needed to map the boundary
 * into world space.
 *
 * Extracted into its own types module (mirrors
 * `objects/objectsAnnotativeTypes.ts`) so the top-level `types.ts`
 * (DXF document shape) can pull the record in via `import type` without
 * re-importing the parser — breaks the depcruise cycle
 * `types.ts` ↔ `objects/spatialFilterParser.ts`. Keep this file
 * types-only (no runtime exports) APART FROM the single placeholder-code
 * constant (a `const`, not a type) shared by the OBJECTS-section
 * parser/writer to avoid a magic-number clone — see
 * `SPATIAL_FILTER_PLACEHOLDER_CODE`.
 *
 * ## Group-code grammar (verified against the ezdxf reference impl
 * `src/ezdxf/entities/spatial_filter.py` AND the Autodesk DXF reference
 * SPATIAL_FILTER page — both agree exactly):
 *
 * ```
 * 0   SPATIAL_FILTER
 * 5   <handle>
 * 330 <ownerHandle>                ← owning extension dictionary
 * 100 AcDbFilter                   ← base subclass marker (empty body)
 * 100 AcDbSpatialFilter            ← concrete subclass marker
 * 70  <boundary point count>       ← number of 10/20 pairs below (>=2)
 * 10/20  <clip boundary point>     ← repeats <count> times, 2D (OCS)
 * 210/220/230 <extrusion>          ← normal of the clip-boundary plane (default 0,0,1)
 * 11/21/31    <origin>             ← origin of clip-boundary local CS (default 0,0,0)
 * 71  <clip boundary display flag> ← is_clipping_enabled (default 1)
 * 72  <has front clipping plane>   ← 0/1 (default 0)
 * 40  <front clip distance>        ← ONLY emitted when 72 == 1
 * 73  <has back clipping plane>    ← 0/1 (default 0)
 * 41  <back clip distance>         ← ONLY emitted when 73 == 1
 * 40 x12  <inverse_insert_matrix>  ← 4x3 column-major; inverse of the INSERT transform
 * 40 x12  <transform_matrix>       ← 4x3 column-major; maps points into the clip-boundary CS
 * ```
 *
 * NOTE the code-40 overload: the front-clip distance (when present) AND
 * the two 12-real matrices all use group 40. On read this is disambiguated
 * positionally exactly like ezdxf does it — the last 24 code-40 reals are
 * the two matrices (inverse then forward); any earlier code-40 real is the
 * front-clip distance, present iff the 72 flag is set. Group 41 (back-clip
 * distance) is distinct. See `parseSpatialFilterEntry`.
 *
 * ## Raw-group preservation — ABSOLUTE SOURCE-ORDINAL model
 *
 * Any group inside the record body that the parser does NOT decode into a
 * typed field (vendor codes, a duplicate handle/owner, an unknown `100`
 * marker, a `102` control block, a non-promoted `41`, a disabled/extra
 * leading `40`, …) is preserved VERBATIM, each stamped with its ABSOLUTE
 * index in the source group array (`SpatialFilterOrdinalRawGroup.ord`). The
 * writer also records the source ordinal of every TYPED group it re-emits
 * (`DxfSpatialFilter.typedOrds`, parallel to its canonical emit order), then
 * MERGES typed-(ord,group) with raw-(ord,group) by ascending ordinal. Because
 * every group — typed OR raw — re-emits at its true source position, a raw
 * group at ANY sub-position (between a `10` and its `20`, inside one of the two
 * `40×12` matrix blocks, after a leading-`40`, …) round-trips byte-identically
 * by construction. This SUPERSEDES the old coarse named-"slot" anchor model
 * (`SpatialFilterRawAnchor`), which could only re-emit a raw group AFTER a
 * named typed field and so was lossy for sub-positions (Codex #319 P2, the 7
 * positional findings).
 *
 * A SYNTHETICALLY constructed record (built in code, not parsed) carries NO
 * ordinals (`rawGroupsOrdinal` / `typedOrds` both absent): the writer falls
 * back to emitting typed groups in canonical order then appending the flat
 * `rawGroups` at the end (the only sensible order when source position is
 * unknown). Re-parsing such a record's output yields a parsed record WITH
 * ordinals.
 */

import type { DxfGroup } from "../dxfPrimitives";

/**
 * Private sentinel group code marking the position of a SPATIAL_FILTER record
 * inside the OBJECTS-section `rawObjectGroups` stream (Codex #319 P2, finding
 * 7). `parseObjectsSection` diverts each SPATIAL_FILTER record into the typed
 * `spatialFilters[]` bucket but ALSO pushes a single
 * `{ code: SPATIAL_FILTER_PLACEHOLDER_CODE, value: <index into spatialFilters[]> }`
 * group into `rawObjectGroups` AT THAT POSITION. `serializeObjectsSection`
 * substitutes the placeholder with the re-synthesised record's groups, so a
 * SPATIAL_FILTER interleaved among unsupported OBJECTS records keeps its
 * relative position instead of being hoisted ahead of the catch-all.
 *
 * The value is `-1`: real DXF group codes are non-negative, so this can never
 * collide with a genuine code. Defined here (a types-module `const`) and
 * imported by BOTH `sectionParsers.ts` and `writer.ts` so the magic number is
 * one truth (no clone).
 */
export const SPATIAL_FILTER_PLACEHOLDER_CODE = -1;

/**
 * Discriminator prefix for the rare degenerate case where a SPATIAL_FILTER
 * record carries NO handle (DXF group 5 — malformed / hand-built). The OBJECTS
 * sentinel normally stores the record's STABLE handle so the writer can resolve
 * the right filter even if `section.spatialFilters[]` is later reordered or
 * filtered (Codex pre-PR — index-staleness). A handle-less record falls back to
 * its positional index, stored as `${PREFIX}${index}`. The prefix begins with
 * `#`, which is NOT a hex digit, so it can never collide with a real handle.
 */
export const SPATIAL_FILTER_PLACEHOLDER_INDEX_PREFIX = "#idx:";

/** 2-D clip-boundary vertex (DXF 10/20). Strictly 2D — no Z per the spec. */
export type SpatialFilterPoint = { x: number; y: number };

/** 3-D point (extrusion 210/220/230 and origin 11/21/31). */
export type SpatialFilter3DPoint = { x: number; y: number; z: number };

/**
 * A preserved raw group together with its ABSOLUTE index in the source group
 * array (`ord`). The writer merges these with the typed groups by ascending
 * `ord` so each raw group re-emits at its EXACT source position — supporting an
 * arbitrary sub-position (inside the boundary run, inside a matrix block, …)
 * that the old coarse named-slot model could not represent (Codex #319 P2).
 *
 * `group` is the ORIGINAL `DxfGroup` object from the source stream, carried by
 * reference — NEVER a rebuilt `{ code, value: formatGroupNumber(parsed) }`. This
 * is load-bearing for the "raw-40 spelling" case: a disabled/extra leading `40`
 * with source value `"4.5000"` or `"1.0E+1"` must re-emit that EXACT string, not
 * a `formatGroupNumber` re-rendering.
 */
export type SpatialFilterOrdinalRawGroup = {
  ord: number;
  group: DxfGroup;
};

/**
 * One clipping-plane: the boolean enable flag plus its signed distance.
 * `enabled` mirrors DXF 72 (front) / 73 (back); `distance` mirrors the
 * conditional DXF 40 (front) / 41 (back) — the distance is only present
 * on disk when the flag is set, but we carry it verbatim either way so
 * the value round-trips (`distance` defaults to 0 when the on-disk tag is
 * absent, matching the ezdxf attribute default).
 */
export type SpatialFilterClipPlane = {
  enabled: boolean;
  distance: number;
};

/**
 * Per-typed-group SOURCE PRESENCE for a PARSED record (Codex #319 P2 — the
 * present-iff-ordinal writer contract). The writer emits a typed group ONLY
 * when its source ordinal was recorded; a record that OMITTED a group (relying
 * on the AutoCAD default) must NOT gain a synthesised tag on re-emit, and a
 * group whose VALUE is defaulted must still be skipped when its source was
 * absent. Each flag here mirrors EXACTLY the parser's `addTyped(...)` gate for
 * that group, so the writer's present-filtered typed list aligns 1:1 with
 * `typedOrds` (length-equal ⇒ the ordinal zip never falls back).
 *
 * Absent on a SYNTHETIC record (built in code, not parsed): the writer then
 * emits the full canonical sequence with documented defaults (e.g. extrusion
 * Z = 1) instead of presence-gating.
 *
 * NOTE: the boundary block, the front/back FLAGS-with-distance, the 71, the
 * group-0 / handle / owner, and the two matrices already have their own
 * presence signals on `DxfSpatialFilter` (`malformedBoundary` / `typedOrds`,
 * `clipActivePresent`, `objectType`/`handle`/`ownerHandle`, the matrix arrays).
 * This struct adds the per-group presence the model did NOT previously carry:
 * the two subclass markers, each extrusion axis, each origin axis, the front
 * flag 72, the back flag 73, the front distance 40, and the back distance 41.
 */
export type SpatialFilterPresence = {
  /** DXF 100 AcDbFilter marker was present in source. */
  subclassFilter: boolean;
  /** DXF 100 AcDbSpatialFilter marker was present in source. */
  subclass: boolean;
  /** DXF 210 / 220 / 230 — per-axis source presence of the extrusion triplet. */
  extrusionX: boolean;
  extrusionY: boolean;
  extrusionZ: boolean;
  /** DXF 11 / 21 / 31 — per-axis source presence of the origin triplet. */
  originX: boolean;
  originY: boolean;
  originZ: boolean;
  /** DXF 72 — front-clip flag was present in source. */
  frontFlag: boolean;
  /** DXF 40 — front-clip distance was present (promoted typed) in source. */
  frontDist: boolean;
  /** DXF 73 — back-clip flag was present in source. */
  backFlag: boolean;
  /** DXF 41 — back-clip distance was present (promoted typed) in source. */
  backDist: boolean;
};

/**
 * A decoded SPATIAL_FILTER record. Every well-known group code is captured
 * as a typed field; any unrecognised group inside the record body is kept
 * verbatim in `rawGroups` / `rawGroupsOrdinal` so the writer can re-emit it
 * (CLAUDE.md priority #1 — full fidelity, no dropped groups).
 */
export type DxfSpatialFilter = {
  /**
   * DXF group 0 — the ORIGINAL record type string as it appeared on disk.
   * Captured verbatim so a vendor-authored object whose group-0 name is NOT
   * the canonical `SPATIAL_FILTER` (but which carries the `AcDbSpatialFilter`
   * subclass marker, so `isSpatialFilterType` accepted it) round-trips with
   * its exact group-0 name instead of being canonicalised to `SPATIAL_FILTER`
   * (Codex #319 P2, finding 3 — preserve-source-never-infer). Optional: a
   * synthetically constructed record (no source group 0) omits it and the
   * writer falls back to the canonical `SPATIAL_FILTER`.
   */
  objectType?: string;
  /** DXF group 5 — handle of the SPATIAL_FILTER record itself. */
  handle: string;
  /**
   * DXF group 330 — handle of the owning extension dictionary (the
   * entity-side anchor: a block reference / VIEWPORT's `ACAD_FILTER`
   * dictionary). Used by SLICE 3 (`resolveViewportClip`) to join the
   * filter back to its owning entity. Optional — degenerate records seen
   * in the wild may omit it.
   */
  ownerHandle?: string;
  /**
   * Clip-boundary polygon (DXF repeating 10/20). The on-disk point count
   * is DXF group 70; `boundaryPoints.length` is authoritative on the typed
   * side (the writer re-derives the 70 count from it). Two points denote a
   * rectangular boundary (lower-left / upper-right); more denote a polyline.
   *
   * Only authoritative when `malformedBoundary` is absent. For a malformed
   * source boundary block (declared 70 ≠ actual 10/20 pair count, or an
   * orphan ordinate) this stays empty and `malformedBoundary` carries the
   * raw block verbatim (preserve-source-never-infer — see below).
   */
  boundaryPoints: SpatialFilterPoint[];
  /**
   * Verbatim boundary block (the DXF 70 count group plus every 10/20
   * ordinate group in source order, including any orphan) — present ONLY
   * when the source boundary block is malformed: the declared 70 count
   * disagrees with the actual number of 10/20 pairs, or an orphan ordinate
   * (a 10 without its 20, or a 20 without its 10) exists.
   *
   * preserve-source-never-infer (CLAUDE.md priority #1): a malformed count
   * must NOT be silently canonicalised to `boundaryPoints.length`. When this
   * field is set the writer re-emits each of its groups verbatim AT its source
   * ordinal (per-group ords in `typedOrds`) so the malformed record is
   * byte-identical through parse → serialize — and an interleaved raw group
   * inside the malformed block still slots correctly. Absent for well-formed
   * records, which keep the typed `boundaryPoints` reconstruction.
   */
  malformedBoundary?: DxfGroup[];
  /**
   * DXF 210/220/230 — normal of the plane containing the clip boundary.
   * Optional; the on-disk default is the Z axis (0,0,1). Captured verbatim
   * only when present so a no-extrusion record round-trips without
   * synthesising a tag AutoCAD did not write.
   */
  extrusion?: SpatialFilter3DPoint;
  /**
   * DXF 11/21/31 — origin used to define the clip-boundary local
   * coordinate system. Optional; on-disk default is (0,0,0). Captured
   * verbatim only when present.
   */
  origin?: SpatialFilter3DPoint;
  /**
   * DXF group 71 — clip boundary display enabled flag (is_clipping_enabled).
   * Default true (the on-disk default when 71 is omitted). `clipActive` is
   * ALWAYS the decoded value for downstream consumers; whether the source
   * actually carried a 71 is tracked by `clipActivePresent` so a 71-omitted
   * record round-trips WITHOUT a synthesised 71 (Codex #319 P2, omitted-71).
   */
  clipActive: boolean;
  /**
   * Whether DXF group 71 was PRESENT in the source. Optional: a parsed record
   * sets it `true` when a 71 was seen and leaves it `undefined`/false when the
   * source omitted 71 (the writer then skips emitting a 71). A synthetic record
   * leaves it `undefined`; the writer's synthetic fallback always emits a 71
   * (canonical AutoCAD output). Distinct from `clipActive`, which is the decoded
   * flag VALUE (default true when absent).
   */
  clipActivePresent?: boolean;
  /** DXF 72 + conditional 40 — front clipping plane flag + distance. */
  frontClip: SpatialFilterClipPlane;
  /** DXF 73 + conditional 41 — back clipping plane flag + distance. */
  backClip: SpatialFilterClipPlane;
  /**
   * DXF 40 (×12) — inverse of the block-reference (INSERT) transform, as a
   * 4×3 matrix in column-major order (exactly 12 reals: column 0 = [0..3],
   * column 1 = [4..7], column 2 = [8..11]). This is the first of the two
   * trailing 12-real code-40 blocks. SLICE 3 multiplies boundary points by
   * this to map them block→WCS.
   */
  inverseInsertMatrix: number[];
  /**
   * DXF 40 (×12) — forward transform that maps points into the clip-
   * boundary coordinate system, 4×3 column-major (exactly 12 reals). This
   * is the second of the two trailing 12-real code-40 blocks. Preserved
   * verbatim (preserve-source-never-infer) even though SLICE 3's resolver
   * only consumes the inverse matrix.
   */
  transformMatrix: number[];
  /**
   * Group codes the parser did not recognise inside the record body, kept
   * verbatim so the writer can re-emit them. Required by CLAUDE.md
   * priority #1 (no dropped groups for vendor / future-AutoCAD codes).
   *
   * Flat projection of `rawGroupsOrdinal` (same groups, ascending `ord`
   * order). Kept as a plain `DxfGroup[]` for downstream ergonomics +
   * back-compat (and as the SYNTHETIC-record raw channel, which has no
   * ordinals); the writer prefers the ordinal form for positional re-emission.
   */
  rawGroups?: DxfGroup[];
  /**
   * The same preserved raw groups as `rawGroups`, each carrying its ABSOLUTE
   * source ordinal (Codex #319 P2 — ordinal model). The writer merges these
   * with the typed groups (positioned via `typedOrds`) by ascending `ord` so
   * EVERY raw group re-emits at its EXACT source position, including arbitrary
   * sub-positions the old named-slot model could not represent. Present on a
   * PARSED record whenever raw groups exist; ABSENT on a synthetic record
   * (which carries only the flat `rawGroups`).
   */
  rawGroupsOrdinal?: SpatialFilterOrdinalRawGroup[];
  /**
   * The ABSOLUTE source ordinal of EACH typed group, in the exact order the
   * writer emits its typed groups (0, 5, 330, both 100 markers, the boundary
   * block — synthesised 70 + points OR per-group malformed-boundary groups, the
   * extrusion triplet, the origin triplet, the 71 IFF present, 72, the front 40
   * IFF enabled, 73, the back 41 IFF the typed distance is set, the inverse
   * matrix ×12, the forward matrix ×12). The writer zips each typed group with
   * `typedOrds[i]` and merges with `rawGroupsOrdinal` by ascending ord. Present
   * on a PARSED record; ABSENT on a synthetic record (the writer then uses pure
   * canonical order + a trailing flat-`rawGroups` append).
   *
   * The writer builds its typed-group list PRESENCE-GATED (see `presence`): a
   * group is emitted IFF its source ordinal exists, so the emitted-typed count
   * equals `typedOrds.length` and the zip never falls back to the append path
   * (Codex #319 P2 — present-iff-ordinal contract).
   */
  typedOrds?: number[];
  /**
   * Per-typed-group SOURCE PRESENCE for a PARSED record (Codex #319 P2). The
   * writer reads this to emit each typed group ONLY when its source ordinal was
   * recorded — never synthesising an omitted flag / distance / extrusion-axis
   * (findings 1–5). Mirrors the parser's `addTyped` gating exactly. Present on a
   * PARSED record (alongside `typedOrds`); ABSENT on a synthetic record, which
   * triggers the writer's canonical full-emission fallback (with documented
   * defaults, e.g. extrusion Z = 1).
   */
  presence?: SpatialFilterPresence;
};
